import React, { useState } from 'react';

import { Tooltip, Card, CardContent, Grid, useMediaQuery, Link } from '@material-ui/core';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { makeStyles } from '@material-ui/core/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';

import {
  COLOR_BLUE_BLACK_LIGHT,
  COLOR_GOLD,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_RED,
} from 'src/const';
import { handleMarketRecommendationClick, calculateSmartDistance } from 'src/utils/functions';
import { MarketReccomendationPointerPropTypes } from 'src/interfaces/iproptypes';
import { Divider, Typography, Box } from '@mui/material';

const useStylesRoot = makeStyles({
  tooltip: {
    padding: 0,
    width: 400,
  },
  tooltipIcon: {
    position: 'relative',
    width: '80px',
    height: '30px',
    background: '#fff',
    color: '#000',
    borderRadius: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
    transform: 'translate(-50%, -50%)',
    transition: 'background 0.3s ease, color 0.3s ease',
    '&:hover': {
      background: '#000',
      color: '#fff',
    },
  },
});

const MarketRecommendationPointer = ({
  lat,
  lng,
  data,
  searchedAddress,
  isTopMarket,
  isHighlighted,
}: MarketReccomendationPointerPropTypes) => {
  const matches = useMediaQuery('(max-width:550px)');
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const rootClass = useStylesRoot();
  const icon = (
    <Box
      sx={{
        position: 'relative',
        width: '80px',
        height: '30px',
        background: isHighlighted ? '#000' : '#fff',
        color: isHighlighted ? '#fff' : '#000',
        borderRadius: 100,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
        transform: 'translate(-50%, -50%)',
        transition: 'background 0.3s ease, color 0.3s ease',
        '&:hover': {
          background: isHighlighted ? '#fff' : '#000',
          color: isHighlighted ? '#000' : '#fff',
        },
      }}
    >
      {isTopMarket && (
        <div
          style={{
            width: 25,
            height: 25,
            borderRadius: 50,
            background: 'black',
            position: 'absolute',
            top: -10,
            left: -10,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            style={{ width: '100%', height: '100%' }}
          >
            <Typography style={{ fontSize: 14 }}>🏆</Typography>
          </Grid>
        </div>
      )}
      <Typography variant="body1" sx={{ fontWeight: 900 }}>
        {data.investmentOptions[0].grossRentalYield}%
      </Typography>
    </Box>
  );

  return (
    <>
      <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
        <Tooltip
          interactive
          classes={rootClass}
          open={tooltipOpen}
          onOpen={() => {
            setTooltipOpen(true);
          }}
          onClose={() => {
            !matches && setTooltipOpen(false);
          }}
          style={{ zIndex: 999999 }}
          title={
            <Card>
              <CardContent style={{ zIndex: 999999 }}>
                <Grid container direction="column" alignItems="flex-start" spacing={2}>
                  <Grid item>
                    <Typography
                      variant="h6"
                      style={{ fontWeight: 'bold', color: COLOR_PRIMARY_DARK }}
                    >
                      {data.district}, {data.city}
                    </Typography>
                  </Grid>

                  <Grid item>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                      {data.countryCode === 'US' ? data.distanceInMiles : data.distanceInKM}{' '}
                      {data.countryCode === 'US' ? 'mi' : 'km'} away from {searchedAddress}
                    </Typography>
                  </Grid>

                  <Grid item>
                    <Typography variant="body1" style={{ color: COLOR_PRIMARY, fontWeight: 600 }}>
                      Gross Rental Yield
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ color: COLOR_BLUE_BLACK_LIGHT, fontWeight: 'bold' }}
                    >
                      {data.investmentOptions[0].grossRentalYield}%
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Link
                      onClick={() => handleMarketRecommendationClick(data)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Typography variant="body1" style={{ color: COLOR_PRIMARY, fontWeight: 600 }}>
                        See Now <OpenInNewIcon style={{ fontSize: 12 }} />
                      </Typography>
                    </Link>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          }
        >
          {icon}
        </Tooltip>
      </ClickAwayListener>
    </>
  );
};

export default React.memo(MarketRecommendationPointer);
